/**
 * BHS-Modell API service
 */

import { fetchStrapi } from './strapi';

export interface BhsPoint {
  text: string;
}

export interface BhsPillar {
  kuerzel: string;
  titel: string;
  untertitel: string;
  beschreibung: string;
  icon: string;
  akzentFarbe: string;
  punkte: BhsPoint[];
}

export interface BhsTag {
  text: string;
}

export interface BhsBenefit {
  text: string;
  icon: string;
}

export interface BhsMethod {
  text: string;
  icon: string;
}

export interface BhsModell {
  badgeText: string;
  heading: string;
  heroDescription: string;
  primaryButtonText: string;
  primaryButtonLink: string;
  pillarsBadge: string;
  pillarsHeading: string;
  pillarsSubtitle: string;
  pillars: BhsPillar[];
  syntheseBadge: string;
  syntheseHeading: string;
  syntheseDescription: string;
  syntheseTags: BhsTag[];
  nutzenBadge: string;
  nutzenHeading: string;
  nutzenSubtitle: string;
  benefits: BhsBenefit[];
  methodenBadge: string;
  methodenHeading: string;
  methodenSubtitle: string;
  methods: BhsMethod[];
  closingQuote: string;
  closingAuthor: string;
  closingPrimaryButtonText: string;
  closingPrimaryButtonLink: string;
  closingSecondaryButtonText: string;
  closingSecondaryButtonLink: string;
}

/**
 * Fetch BHS-Modell data from Strapi
 */
export async function fetchBhsModell(): Promise<BhsModell | null> {
  try {
    const response = await fetchStrapi<any>(
      '/bhs-modell?populate[pillars][populate][0]=punkte&populate[syntheseTags]=true&populate[benefits]=true&populate[methods]=true'
    );

    if (!response.data) {
      return null;
    }

    const data = response.data;

    return {
      badgeText: data.badgeText || '',
      heading: data.ueberschrift || '',
      heroDescription: data.heroBeschreibung || '',
      primaryButtonText: data.primaerButtonText || '',
      primaryButtonLink: data.primaerButtonLink || '',
      pillarsBadge: data.pillarsBadge || '',
      pillarsHeading: data.pillarsUeberschrift || '',
      pillarsSubtitle: data.pillarsUntertitel || '',
      pillars: (data.pillars || []).map((p: any) => ({
        kuerzel: p.kuerzel || '',
        titel: p.titel || '',
        untertitel: p.untertitel || '',
        beschreibung: p.beschreibung || '',
        icon: p.icon || '',
        akzentFarbe: p.akzentFarbe || '',
        punkte: (p.punkte || []).map((pt: any) => ({ text: pt.text || '' })),
      })),
      syntheseBadge: data.syntheseBadge || '',
      syntheseHeading: data.syntheseUeberschrift || '',
      syntheseDescription: data.syntheseBeschreibung || '',
      syntheseTags: (data.syntheseTags || []).map((t: any) => ({ text: t.text || '' })),
      nutzenBadge: data.nutzenBadge || '',
      nutzenHeading: data.nutzenUeberschrift || '',
      nutzenSubtitle: data.nutzenUntertitel || '',
      benefits: (data.benefits || []).map((b: any) => ({
        text: b.text || '',
        icon: b.icon || '',
      })),
      methodenBadge: data.methodenBadge || '',
      methodenHeading: data.methodenUeberschrift || '',
      methodenSubtitle: data.methodenUntertitel || '',
      methods: (data.methods || []).map((m: any) => ({
        text: m.text || '',
        icon: m.icon || '',
      })),
      closingQuote: data.closingZitat || '',
      closingAuthor: data.closingAutor || '',
      closingPrimaryButtonText: data.closingPrimaerButtonText || '',
      closingPrimaryButtonLink: data.closingPrimaerButtonLink || '',
      closingSecondaryButtonText: data.closingSekundaerButtonText || '',
      closingSecondaryButtonLink: data.closingSekundaerButtonLink || '',
    };
  } catch (error) {
    console.error('Error fetching BHS-Modell from Strapi:', error);
    return null;
  }
}
